/**
 * #725 — subscribe a teaching surface to the SIGNED-IN trader's key mapping.
 *
 * Any control that names a key reads it through here, so a remap updates the
 * chip, the readout, the tooltips, and the receipts in the same render as the
 * setting itself — "visible before the trader returns to the trading surface"
 * without a reload.
 *
 * Every read is ownership-gated against the current auth user, so a mapping
 * belonging to a previously signed-in trader can never reach the screen or the
 * keyboard; it degrades to the shipped defaults instead. See
 * `stores/liveHotkeys`.
 *
 * `hooks/` has no barrel; import directly.
 */
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { twoSidedOptedIn } from '@/lib/trading/twoSidedOptIn';
import { useLiveHotkeysStore, selectOwnedHotkeys } from '@/stores/liveHotkeys';
import {
  hotkeyLabelFor,
  hotkeyPairLabel,
  isActionUnbound,
  type LiveHotkeyActionId,
  type LiveHotkeyGrants,
  type LiveHotkeyMap,
} from '@/lib/trading/liveHotkeyRegistry';

/**
 * The viewer's grants, subscribed so a teaching surface stops naming a key the
 * moment the capability behind it goes away. Resolved HERE rather than at each
 * call site for the same reason the ownership gate is: a second copy of this
 * rule is a second copy to get wrong.
 */
export function useLiveHotkeyGrants(): LiveHotkeyGrants {
  const attack = useAuthStore((s) => s.user?.attackAccess ?? false);
  // #724 — the trader's own opt-in rides with the grants so a teaching
  // surface names S only while S actually fires — for THIS trader.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const twoSided = useDisplaySettings((s) => twoSidedOptedIn(s, viewerId));
  return { attack, twoSided };
}

/** The viewer's own mapping, for a surface naming more than one key. */
export function useLiveHotkeyMap(): LiveHotkeyMap {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  return useLiveHotkeysStore((s) => selectOwnedHotkeys(s, viewerId));
}

/** One action's key, ready to render: "A", "Spacebar", or "—" when unbound. */
export function useHotkeyLabel(id: LiveHotkeyActionId): string {
  return hotkeyLabelFor(useLiveHotkeyMap(), id, useLiveHotkeyGrants());
}

/**
 * Is this action active for the viewer but reachable by no key (#740)?
 *
 * A surface that TEACHES a key asks this to explain the em dash
 * `useHotkeyLabel` returns; an ungranted or dormant action answers false, so
 * the explanation can never leak a capability the trader does not hold.
 */
export function useHotkeyUnbound(id: LiveHotkeyActionId): boolean {
  return isActionUnbound(useLiveHotkeyMap(), id, useLiveHotkeyGrants());
}

/** Two actions as the product says them, e.g. "A/D" or "W/E". */
export function useHotkeyPairLabel(
  first: LiveHotkeyActionId,
  second: LiveHotkeyActionId,
): string {
  return hotkeyPairLabel(useLiveHotkeyMap(), first, second, useLiveHotkeyGrants());
}

/**
 * The mapping for a NON-REACT reader (the key listener), gated the same way.
 *
 * Kept beside the hooks so the ownership rule has one home: a second gate
 * written at a call site is a second gate to get wrong.
 */
export function readOwnedHotkeys(): LiveHotkeyMap {
  const viewerId = useAuthStore.getState().user?.id ?? null;
  return selectOwnedHotkeys(useLiveHotkeysStore.getState(), viewerId);
}
